import React, { createRef, ReactNode, RefObject, useContext, useEffect, useState } from "react";
import "amazon-connect-streams";
import { getLogger, Logger } from "../services/LoggingService";
import { CallContext } from "../contexts/CallContext";
import AppContext from "../contexts/AppContextProvider";
function link(url: string, label: string) {
    return <a href={url}>{label}</a>;
}

interface IProps {
    ccpVisible: boolean;
    onInitialized: () => void;
}

const ccpRef: RefObject<HTMLDivElement> = createRef();

export function Ccp(props: IProps) {
    const logger: Logger = getLogger("Ccp");
    const [error, setError] = useState<ReactNode>("");

    const { config } = useContext(AppContext);
    const { setAgent, setContact } = useContext(CallContext);

    useEffect(() => {
        if ((connect.core as any).initialized) {
            logger.debug("ccp already initialized");
            return;
        }

        logger.debug("init ccp called");
        const params: any = {
            ccpUrl: config.ccpUrl,
            loginPopup: true,
            loginPopupAutoClose: true,
            region: config.ccpRegion,
            softphone: {
                allowFramedSoftphone: true,
                enableAudioDeviceSettings: true,
                ringtoneUrl: `${window.location.origin}/ringtone.mp3`,
            },
        };

        // Configure SSO
        if (config.samlSsoUrl.length > 0) {
            params.loginUrl = config.samlSsoUrl;
        }

        connect.core.initCCP(ccpRef.current as HTMLElement, params);

        connect.contact((contact: connect.Contact) => {
            logger.debug("contact id " + contact.getContactId());
            setContact(contact);
        });

        connect.agent((agent: connect.Agent) => {
            setAgent(agent);
            agent.onError((a: any) => logger.error("onAgentError", a));
            agent.onSoftphoneError((a: any) => onSoftphoneError(a));
            window.addEventListener("beforeunload", (event) => {
                let states = agent.getAgentStates();
                let offlineState = states.filter((state) => state.name === "Offline")[0];
                agent.setState(offlineState, {
                    success: () => {},
                    failure: () => {},
                });
            });
        });

        // Un-mount action
        return () => connect.core.terminate();
    }, []);

    const onSoftphoneError = (a: { errorType: string; errorMessage: string; endPointUrl: string }) => {
        logger.error("onSoftphoneError", a);
        if (a.errorType === "unsupported_browser") {
            setError(
                <p>
                    Only the latest 3 versions of Chrome or Firefox are supported. Upgrade your browser to resolve this
                    error.
                </p>
            );
        } else if (a.errorType === "microphone_not_shared") {
            setError(
                <div>
                    <p>
                        The microphone is not accessible, please reload the page and allow microphone access when
                        prompted.
                    </p>
                    <p>
                        Lastly, you can try using {link("https://www.mozilla.org/en-US/firefox/new/", "Firefox")} or{" "}
                        {link("https://www.google.com/chrome/", "Chrome")}.
                    </p>
                </div>
            );
        } else if (a.errorType === "user_busy_error") {
            logger.error(a.errorType + ": " + a.errorMessage);
            setError("");
        } else {
            setError(<p>{a.errorType}</p>);
        }
    };

    return (
        <div id="ccpFrame" className={config.isV2() ? "ccp-wrapper v2" : "ccp-wrapper"}>
            <div className={!props.ccpVisible ? "ccp-frame collapsed" : "ccp-frame"} ref={ccpRef} />
            {error && <div className="error">{error}</div>}
        </div>
    );
}
